import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { IoNotificationsOutline, IoClose } from 'react-icons/io5';
import i18next from 'i18next';
import { useNotifications } from '../../context/NotificationContextProvider';
import { trackEvent } from '../../utils/analytics';
import './NewsRollDrawer.css';

const formatTime = (timestamp) => {
  const d = new Date(timestamp * 1000);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const NewsRollDrawer = () => {
  const [newsRoll, setNewsRoll] = useState([]);
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const { liveVideo } = useNotifications() || {};

  const latestTimeRef = useRef(0);
  const lastSeenTimeRef = useRef(0);
  const liveRollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const fetchNewsRoll = async () => {
      if (!window.liveRoll || !window.liveRollID) return;
      try {
        const response = await axios.post(window.liveRoll, {
          i: window.liveRollID,
          l: window.liveRollLines,
          t: latestTimeRef.current,
        });
        latestTimeRef.current = 1;
        if (!cancelled) setNewsRoll(response.data.rolls || []);
      } catch {
        // swallow
      } finally {
        if (!cancelled) {
          timer = setTimeout(fetchNewsRoll, window.liveRollInterval || 12000);
        }
      }
    };

    fetchNewsRoll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!newsRoll.length) return;
    const maxTime = newsRoll.reduce((m, r) => Math.max(m, r.time || 0), 0);
    if (open) {
      lastSeenTimeRef.current = maxTime;
      setUnreadCount(0);
    } else {
      const unread = newsRoll.filter(
        (r) => (r.time || 0) > lastSeenTimeRef.current,
      ).length;
      setUnreadCount(unread);
    }
  }, [newsRoll, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    const onOpen = () => {
      trackEvent('newsroll_open');
      setOpen(true);
    };
    window.addEventListener('newsroll:open', onOpen);
    return () => window.removeEventListener('newsroll:open', onOpen);
  }, []);

  return (
    <>
      <button
        type="button"
        className="floating-newsroll-button"
        onClick={() => {
          trackEvent('newsroll_open');
          setOpen(true);
        }}
        aria-label={i18next.t('滚动新闻')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <IoNotificationsOutline />
        {unreadCount > 0 && (
          <span className="floating-badge" aria-label={`${unreadCount} new`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <div
        className={`newsroll-drawer-backdrop ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <aside
        className={`newsroll-drawer ${open ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={i18next.t('滚动新闻')}
      >
        <div className="newsroll-drawer__head">
          <div className="newsroll-drawer__title">
            <h2>{i18next.t('滚动新闻')}</h2>
            <span className="live-pulse">
              <span className="live-pulse__dot" /> LIVE
            </span>
          </div>
          <button
            type="button"
            className="newsroll-drawer__close"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <IoClose />
          </button>
        </div>
        <div className="newsroll-drawer__body">
          {liveVideo?.href && (
            <a
              className="roll-live"
              href={liveVideo.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('video_click', { url: liveVideo.href, title: liveVideo.title, location: 'newsroll_live' })}
            >
              <div className="roll-live__media">
                {liveVideo.image && <img src={liveVideo.image} alt="Live" />}
                <span className="roll-live__badge">
                  <span className="live-pulse__dot" /> LIVE
                </span>
              </div>
              <div className="roll-live__title">
                <span>{liveVideo.title || '直播 / Live Video'}</span>
                <span className="roll-live__cta">{i18next.t('立即观看')} ▶</span>
              </div>
            </a>
          )}
          <ol className="roll-list" aria-live="polite" ref={liveRollRef}>
            {newsRoll.map((roll, i) => (
              <li key={i} className="roll-card">
                <time className="roll-time">{formatTime(roll.time)}</time>
                <div
                  className="roll-content"
                  dangerouslySetInnerHTML={{ __html: roll.c }}
                />
              </li>
            ))}
          </ol>
          {newsRoll.length === 0 && (
            <p className="newsroll-drawer__empty">{i18next.t('暂无滚动新闻')}</p>
          )}
        </div>
        {window.liveRollFullURL && (
          <a
            className="morerollsa newsroll-drawer__more"
            rel="noopener"
            href={window.liveRollFullURL}
          >
            <span className="morerolls">{i18next.t('完整滚动新闻')}</span>
          </a>
        )}
      </aside>
    </>
  );
};

export default NewsRollDrawer;
